/**
 * Presence Store
 *
 * Tracks which users are currently active inside a live prioritization session
 * using Supabase Realtime Presence.  One channel per session is opened when the
 * current user enters a session and torn down on leave.
 *
 * Usage — a screen that puts its viewer in the room:
 *   useSessionPresence(sessionId, userId, userName, 'facilitator');
 * Read who is there with `usePresenceStore(s => s.presentUsers)`.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';

/**
 * Bumped by every join and leave. A join that finds it has changed by the time
 * its turn comes has been overtaken (a leave, or a newer join, was asked for
 * while it waited) and does nothing.
 */
let presenceGeneration = 0;

/**
 * Joins and leaves run one at a time, in the order they were asked for.
 *
 * The realtime client keeps one channel per topic: `channel(topic)` hands back
 * the existing channel when there is one, a closing channel is dropped from the
 * client's list by its TOPIC, and removing the last channel disconnects the
 * socket. So a screen that leaves and rejoins the same session in one breath —
 * every re-render that changes the viewer's name or role does — must not start
 * the new channel until the old one has finished closing, and must close the
 * old one once. Two overlapping closes dropped the new channel from the list
 * and cut the socket under it: "Who's in the room" stayed at zero.
 */
let presenceQueue: Promise<void> = Promise.resolve();
function inTurn(step: () => Promise<void>): Promise<void> {
  const run = presenceQueue.then(step, step);
  presenceQueue = run.catch(() => {});
  return run;
}

export type PresenceRole = 'facilitator' | 'participant' | 'observer';
export type PresenceActivity = 'waiting' | 'reading-brief' | 'ranking' | 'submitted' | 'observing';

export interface PresentUser {
  userId: string;
  userName: string;
  role: PresenceRole;
  joinedAt: string; // ISO string
  /** True when the channel has heard from this user in the last 30 s */
  isOnline: boolean;
  currentActivity?: PresenceActivity;
}

interface PresenceState {
  presentUsers: PresentUser[];
  isConnected: boolean;
  sessionId: string | null;
  currentUserId: string | null;
  error: string | null;

  /** Open a Realtime Presence channel for this session and track the current user. */
  join: (
    sessionId: string,
    userId: string,
    userName: string,
    role?: PresenceRole
  ) => Promise<void>;

  /** Update the current user's activity signal (visible to all participants). */
  updateActivity: (activity: PresenceActivity) => Promise<void>;

  /** Untrack the current user and close the channel. */
  leave: () => Promise<void>;

  /** Internal: close the current channel; shared by join (before reopening) and leave. */
  _teardown: () => Promise<void>;

  /** Internal: replace the full user list from a presence sync event. */
  _syncUsers: (state: Record<string, PresenceMeta[] | { metas?: PresenceMeta[] }>) => void;

  _channel: RealtimeChannel | null;
  _currentMeta: PresenceMeta | null;
}

interface PresenceMeta {
  userId: string;
  userName: string;
  role: PresenceRole;
  joinedAt: string;
  currentActivity?: PresenceActivity;
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  presentUsers: [],
  isConnected: false,
  sessionId: null,
  currentUserId: null,
  error: null,
  _channel: null,
  _currentMeta: null,

  join: (sessionId, userId, userName, role = 'participant') => {
    const generation = ++presenceGeneration;
    return inTurn(async () => {
      const supabase = getSafeSupabaseClient();

      // If already in a session, leave first — and wait for it to finish closing.
      await get()._teardown();
      if (generation !== presenceGeneration) return;

      if (!supabase) {
        // Offline / demo mode: just show the current user locally
        set({
          sessionId,
          currentUserId: userId,
          isConnected: false,
          presentUsers: [
            {
              userId,
              userName,
              role,
              joinedAt: new Date().toISOString(),
              isOnline: true,
            },
          ],
        });
        return;
      }

      set({ sessionId, currentUserId: userId, error: null });

      const channel = supabase.channel(`presence:session:${sessionId}`, {
        config: { presence: { key: userId } },
      });
      set({ _channel: channel });

      channel
        .on('presence', { event: 'sync' }, () => {
          const state = channel.presenceState<PresenceMeta>();
          get()._syncUsers(state);
        })
        .on('presence', { event: 'join' }, ({ newPresences }) => {
          // sync is also fired, but handle join for immediate feedback
          void newPresences;
          const state = channel.presenceState<PresenceMeta>();
          get()._syncUsers(state);
        })
        .on('presence', { event: 'leave' }, ({ leftPresences }) => {
          void leftPresences;
          const state = channel.presenceState<PresenceMeta>();
          get()._syncUsers(state);
        })
        .subscribe(async (status) => {
          // A leave or a newer join has replaced this channel: do not announce on it.
          if (get()._channel !== channel) return;
          if (status === 'SUBSCRIBED') {
            const meta: PresenceMeta = {
              userId,
              userName,
              role,
              joinedAt: new Date().toISOString(),
              currentActivity: role === 'facilitator' ? 'observing' : 'waiting',
            };
            await channel.track(meta);
            if (get()._channel !== channel) return;
            set({ isConnected: true, _currentMeta: meta });
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            set({ isConnected: false, error: `Presence channel ${status}` });
          }
        });
    });
  },

  updateActivity: async (activity) => {
    const { _channel, _currentMeta } = get();
    if (!_channel || !_currentMeta) return;
    const updated: PresenceMeta = { ..._currentMeta, currentActivity: activity };
    try {
      await _channel.track(updated);
      set({ _currentMeta: updated });
    } catch {
      // Ignore — presence update is best-effort
    }
  },

  leave: () => {
    presenceGeneration++;
    return inTurn(() => get()._teardown());
  },

  _teardown: async () => {
    const { _channel } = get();
    const supabase = getSafeSupabaseClient();
    // Let go of the channel before closing it, so nothing else closes it again.
    set({
      _channel: null,
      _currentMeta: null,
      presentUsers: [],
      isConnected: false,
      sessionId: null,
      currentUserId: null,
    });
    if (!_channel) return;
    try {
      await _channel.untrack();
      if (supabase) await supabase.removeChannel(_channel);
    } catch {
      // Ignore cleanup errors
    }
  },

  _syncUsers: (state) => {
    const users: PresentUser[] = [];
    for (const presences of Object.values(state)) {
      // channel.presenceState() hands back one array of presences per key: the
      // client strips the wire format's `metas` wrapper. This read `.metas`
      // off that array, found nothing, and the room was always empty.
      const metas = Array.isArray(presences) ? presences : presences.metas ?? [];
      for (const meta of metas) {
        users.push({
          userId: meta.userId,
          userName: meta.userName,
          role: meta.role ?? 'participant',
          joinedAt: meta.joinedAt ?? new Date().toISOString(),
          isOnline: true,
          currentActivity: meta.currentActivity,
        });
      }
    }
    set({ presentUsers: users });
  },
}));

/**
 * Put the viewer in a session's room for as long as the calling screen is
 * open: join the session's presence channel on mount (and again when any
 * argument changes), leave it on unmount. Pass no sessionId or userId to stay
 * out. This is what "Who's in the room" counts.
 */
export function useSessionPresence(
  sessionId: string | null | undefined,
  userId: string | null | undefined,
  userName: string,
  role: PresenceRole = 'participant',
): void {
  const join = usePresenceStore((s) => s.join);
  const leave = usePresenceStore((s) => s.leave);
  useEffect(() => {
    if (!sessionId || !userId) return;
    void join(sessionId, userId, userName, role);
    return () => { void leave(); };
  }, [sessionId, userId, userName, role, join, leave]);
}
